import { type FC, type ChangeEvent, useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Box,
  Button,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Grid,
  IconButton,
  type AlertColor,
  type AlertPropsColorOverrides,
  InputAdornment,
} from '@mui/material';
import type { OverridableStringUnion } from '@mui/types';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { PostgrestSingleResponse } from '@supabase/supabase-js';
import { useForm, Controller, useFieldArray } from 'react-hook-form';

import PageBaseLayout from '../layouts/PageBaseLayout';
import {
  filesAreIdenticalUnordered,
  getBase64CatImageFile,
  imageUrlsToFiles,
  toTitleCase,
} from '../utils';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import type { Campaign, Merchant } from '../models/types';
import { createCampaign, getCampaignImages, updateCampaign } from '../fetchFns';
import { supabase } from '../supabase-client';
import moment from 'moment';

/** A single (possibly nested) term as stored on the backend. */
type TermNode = { text: string | null; children?: TermNode[] | null };

/**
 * Renders the backend `terms_and_conditions` tree (array of {text, children}).
 * Top-level items are bold decimal headings; nested levels use lower-alpha /
 * lower-roman, mirroring the hardcoded preview. Empty (null/blank) nodes are
 * skipped.
 */
const TermsList: FC<{ terms: unknown; depth?: number }> = ({
  terms,
  depth = 0,
}) => {
  const arr = Array.isArray(terms) ? (terms as TermNode[]) : [];
  const items = arr.filter(
    (t) => t && t.text != null && String(t.text).trim().length > 0,
  );
  if (items.length === 0) return null;

  const listStyleType =
    depth === 0 ? 'decimal' : depth === 1 ? 'lower-alpha' : 'lower-roman';

  return (
    <ol style={{ listStyleType }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontWeight: depth === 0 ? 'bold' : 'normal' }}>
          {t.text}
          {t.children ? (
            <TermsList terms={t.children} depth={depth + 1} />
          ) : null}
        </li>
      ))}
    </ol>
  );
};

type FormValues = {
  merchantId: number;
  name: string;
  type: 'voucher' | 'product';
  endDate: string;
  voucherLifespanDays: number;
  maxVouchers: number;
  minimumSpend: number;
  affiliateEarning: number;
  refereeBenefitType: 'amount' | 'item';
  refereeBenefitAmount: number;
  refereeBenefitDescription: string;
  trandingCommission: number;
  termsAndConditions: string;
  voucherTermsAndConditions: { term: string }[];
  description: string;
  images: File[]; // Add images field
};

const defaultValues: FormValues = {
  // merchantId: 1,
  // name: 'testname',
  // type: 'voucher',
  // endDate: '2026-12-15',
  // voucherLifespanDays: 450,
  // maxVouchers: 60,
  // minimumSpend: 5,
  // affiliateEarning: 230,
  // refereeBenefitType: 'amount',
  // refereeBenefitAmount: 5,
  // refereeBenefitDescription: 'testdescription',
  // trandingCommission: 0,
  // termsAndConditions: 'fwe',
  // voucherTermsAndConditions: [{ term: '' }],
  // description: 'jrmkoj',
  // images: [getBase64CatImageFile()],
  merchantId: 1,
  name: '',
  type: 'voucher',
  endDate: '',
  voucherLifespanDays: 0,
  maxVouchers: 0,
  minimumSpend: 0,
  affiliateEarning: 0,
  refereeBenefitType: 'amount',
  refereeBenefitAmount: 0,
  refereeBenefitDescription: '',
  trandingCommission: 0,
  termsAndConditions: '',
  voucherTermsAndConditions: [{ term: '' }],
  description: '',
  images: [],
};

const refereeBenefitType = ['amount', 'item'];

const CreateNewCampaign: FC<{ updateMode?: boolean }> = ({
  updateMode = false,
}) => {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const [merchants, setMerchants] = useState<Merchant[]>([
    {
      id: 1,
      name: '',
      email: '',
      caption: '',
      category_id: 0,
      category: '',
      subcategory_id: 0,
      subcategory: '',
      logoUrl: '',
      priority: 1,
    },
  ]);
  const [originalImages, setOriginalImages] = useState<File[]>([]);
  const campaignData: Campaign = useLocation().state;
  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'voucherTermsAndConditions', // unique name for your Field Array
  });
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const images = watch('images');

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    backdrop.handleBackdropOpen();

    // Snapshot the selection up front. Resetting the input below empties the
    // live FileList, so we must copy the files before that happens.
    const selectedFiles = Array.from(files);
    // Reset once (not inside a loop) so the same file can be picked again.
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      // Make sure every picked file is a loadable image.
      await Promise.all(
        selectedFiles.map(
          (imgFile) =>
            new Promise<void>((resolve, reject) => {
              const img = new window.Image();
              img.src = URL.createObjectURL(imgFile);
              img.onload = () => resolve();
              img.onerror = () =>
                reject(new Error(`Error loading image: ${imgFile.name}`));
            }),
        ),
      );

      // Read the latest value (getValues, not the stale `images` closure) and
      // append ALL selected files at once so nothing is lost.
      const currentImages = getValues('images') || [];
      const newImages = currentImages.concat(selectedFiles).slice(0, 5);
      setValue('images', newImages, { shouldValidate: true });
    } catch (error) {
      snackbar.handleSnackbarOpen((error as Error).message, 'error');
    } finally {
      backdrop.handleBackdropClose();
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...(images || [])];
    newImages.splice(index, 1);
    setValue('images', newImages, { shouldValidate: true });
  };

  const onSubmit = async (data: FormValues) => {
    let message = `Campaign has been ${updateMode ? 'updated' : 'created'}.`;
    let status:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    backdrop.handleBackdropOpen();
    try {
      const formData = new FormData();

      formData.append('merchant_id', data.merchantId.toString());
      formData.append('name', data.name);
      formData.append('type', data.type);
      formData.append('end_date', data.endDate);
      formData.append(
        'voucher_lifespan_days',
        data.voucherLifespanDays.toString(),
      );
      formData.append('max_vouchers', data.maxVouchers.toString());
      formData.append('minimum_spend', data.minimumSpend.toString());
      formData.append('affiliate_earning', data.affiliateEarning.toString());
      formData.append('referee_benefit_type', data.refereeBenefitType);
      formData.append(
        'referee_benefit_amount',
        data.refereeBenefitAmount.toString(),
      );
      formData.append(
        'referee_benefit_description',
        data.refereeBenefitDescription,
      );
      // trandingCommission is entered as %, convert to decimal here
      formData.append(
        'tranding_commission',
        (data.trandingCommission / 100).toString(),
      );
      formData.append('terms_and_conditions', data.termsAndConditions);
      formData.append(
        'voucher_terms_and_conditions',
        JSON.stringify(data.voucherTermsAndConditions.map((el) => el.term)),
      );
      formData.append('description', data.description);
      // Append each image file individually

      const campaignImagesHaveChanged = !(await filesAreIdenticalUnordered(
        data.images,
        originalImages,
      ));
      if (campaignImagesHaveChanged) {
        data.images.forEach((file) => {
          formData.append('images', file, file.name);
        });
      }

      if (updateMode) {
        formData.append('campaign_id', campaignData.id.toString());
        const response = await updateCampaign(formData);
      } else {
        const response = await createCampaign(formData);
      }

      reset();
      navigate('/dashboard/campaigns');
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast);
      message = errorCast.message;
      status = 'error';
    }
    snackbar.handleSnackbarOpen(message, status);
    backdrop.handleBackdropClose();
  };

  const getData = async () => {
    backdrop.handleBackdropOpen();
    try {
      const response = await supabase.from('merchants').select('*');

      if (response.error) {
        console.error(response.error);
        throw new Error(response.error.message);
      }
      setMerchants(response.data);

      if (updateMode) {
        const [campaignResponse, campaignImagesResponse] = await Promise.all([
          supabase
            .schema('admin_backend')
            .from('campaigns_admin_view')
            .select('*')
            .eq('id', campaignData.id)
            .single(),
          getCampaignImages(campaignData.merchant_id, campaignData.id),
        ]);

        if (campaignImagesResponse.error) {
          console.error(campaignImagesResponse.error);
          throw new Error(campaignImagesResponse.error);
        }
        if (campaignResponse.error) {
          console.error(campaignResponse.error);
          throw new Error(campaignResponse.error.message);
        }

        const imageUrls = (
          campaignImagesResponse as PostgrestSingleResponse<any[]>
        ).data!.map((el) => el.url);

        const imageFiles = await imageUrlsToFiles(imageUrls);
        setOriginalImages(imageFiles);

        setValue('images', imageFiles);

        setFields(campaignResponse.data);
      }
    } catch (error) {
      const errorCast = error as Error;

      console.error(errorCast.stack);
      console.error(errorCast.name);
      snackbar.handleSnackbarOpen(String(error), 'error');
    }
    backdrop.handleBackdropClose();
  };

  const setFields = (campaignData: Campaign) => {
    setValue('merchantId', campaignData.merchant_id);
    setValue('name', campaignData.name);
    setValue('endDate', campaignData.end_date);
    setValue('voucherLifespanDays', campaignData.voucher_lifespan_days);
    setValue('maxVouchers', campaignData.max_vouchers);
    setValue('minimumSpend', campaignData.minimum_spend);
    setValue('affiliateEarning', campaignData.affiliate_earning);
    setValue('refereeBenefitType', campaignData.referee_benefit_type);
    setValue(
      'refereeBenefitDescription',
      campaignData.referee_benefit_description,
    );
    setValue('refereeBenefitAmount', campaignData.referee_benefit_amount ?? 0);
    setValue('termsAndConditions', campaignData.terms_and_conditions);
    setValue(
      'voucherTermsAndConditions',
      campaignData.voucher_terms_and_conditions.map((term) => ({
        term: term.text,
      })),
    );
    setValue('description', campaignData.description);
    setValue('trandingCommission', campaignData.commission * 100);
  };

  useEffect(() => {
    getData();
  }, []);

  const refereeBenefitTypeWatch = watch('refereeBenefitType');

  useEffect(() => {
    if (refereeBenefitTypeWatch === 'item') {
      setValue('refereeBenefitAmount', 0);
    }
  }, [refereeBenefitTypeWatch]);

  return (
    <PageBaseLayout
      title={
        updateMode ? 'Update Voucher Campaign' : 'Create New Voucher Campaign'
      }>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={3}>
          <Controller
            name="merchantId"
            control={control}
            rules={{ required: 'Brand is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                disabled={updateMode}
                select
                label="Brand"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                fullWidth>
                {merchants.map((merchant) => (
                  <MenuItem
                    disabled={updateMode}
                    key={merchant.id}
                    value={merchant.id}>
                    {merchant.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Campaign name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Campaign Name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                fullWidth
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            rules={{ required: 'End date is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="End Date"
                type="date"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Campaign Description"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                multiline
                rows={3}
                fullWidth
              />
            )}
          />
          <Controller
            name="affiliateEarning"
            control={control}
            rules={{
              required: 'Affiliate earning is required',
              min: {
                message: 'Affiliate earning must be 0 or larger',
                value: 0,
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Affiliate Commission"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                fullWidth
                slotProps={{
                  htmlInput: { min: 0 },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="refereeBenefitType"
              control={control}
              rules={{
                required: 'Referee benefit type is required',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  label="Referee Voucher Benefit Type"
                  type="text"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { min: 0 } }}>
                  {refereeBenefitType.map((type) => (
                    <MenuItem key={type} value={type}>
                      {toTitleCase(type)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="refereeBenefitAmount"
              control={control}
              rules={{
                required: 'Referee benefit amount is required',
                min: {
                  message: 'Affiliate earning must be 0 or larger',
                  value: 0,
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  disabled={watch('refereeBenefitType') === 'item'}
                  label="Referee Benefit Amount"
                  type="number"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                  fullWidth
                  slotProps={{
                    htmlInput: { min: 0 },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Stack>
          <Controller
            name="refereeBenefitDescription"
            control={control}
            rules={{
              required: 'Referee benefit description is required',
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Referee Benefit Description"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                multiline
                rows={3}
                fullWidth
              />
            )}
          />
          <Controller
            name="trandingCommission"
            control={control}
            rules={{
              required: 'Tranding commission is required',
              min: {
                message: 'Tranding commission must be 0 or larger',
                value: 0,
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Tranding Commission %"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                fullWidth
                slotProps={{
                  htmlInput: { min: 0 },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <TextField
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            disabled
            value={(
              (parseFloat(watch('trandingCommission').toString()) / 100) *
              (parseFloat(watch('affiliateEarning').toString()) +
                parseFloat(watch('refereeBenefitAmount').toString()))
            ).toFixed(2)}
            label="Tranding Commission Calculated"
            type="number"
            helperText="Commission to Tranding per voucher redemption. Formula: Tranding Commission % &times; Affiliate Earning"
            slotProps={{
              htmlInput: { min: 0 },
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
          <Controller
            name="minimumSpend"
            control={control}
            rules={{
              required: 'Valid budget is required',
              min: {
                message: 'Min. spend must be 0 or larger',
                value: 0,
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Minimum Spend"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                fullWidth
                slotProps={{ htmlInput: { min: 0 } }}
              />
            )}
          />
          <Controller
            name="maxVouchers"
            control={control}
            rules={{
              required: 'Max. no of vouchers required',
              min: {
                message: 'Max no. of vouchers must be at least 1',
                value: 1,
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Max. Generated Vouchers Per User Per Campaign"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error && fieldState.error.message}
                required
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { min: 1 },
                }}
                fullWidth
              />
            )}
          />
          <Controller
            name="voucherLifespanDays"
            control={control}
            rules={{
              required: 'Voucher lifespan is required',
              min: {
                message: 'Voucher lifespan must be at least 1 day',
                value: 1,
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Voucher Lifespan (Days)"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { min: 1 },
                }}
                fullWidth
              />
            )}
          />
          <Typography variant="h6">Campaign Terms & Conditions</Typography>
          {updateMode ? (
            // In update mode show the actual terms_and_conditions fetched from
            // the backend instead of a generated preview.
            <TermsList terms={watch('termsAndConditions')} />
          ) : (
            <ol>
              <b>
                <li>Participation</li>
              </b>
              <ol style={{ listStyleType: 'lower-alpha' }}>
                <li>
                  Promote{' '}
                  {
                    merchants.find(
                      (merchant) => merchant.id === watch('merchantId'),
                    )?.name
                  }{' '}
                  by sharing your experience and affiliate link with your social
                  network. By participating, you agree to these
                  campaign-specific terms and Tranding’s general Terms &
                  Conditions. Tranding and{' '}
                  {
                    merchants.find(
                      (merchant) => merchant.id === watch('merchantId'),
                    )?.name
                  }{' '}
                  reserve the right to modify or terminate this campaign at any
                  time.
                </li>
              </ol>
              <b>
                <li>Commission Structure</li>
              </b>
              <ol style={{ listStyleType: 'lower-alpha' }}>
                <li>
                  Affiliate Earnings: You will earn ${watch('affiliateEarning')}{' '}
                  for each successful referral.
                </li>
                <li>
                  Successful Referral: A successful referral is defined as the
                  successful redemption of a voucher generated from your unique
                  affiliate link or code prior to expiration.
                </li>
              </ol>
              <b>
                <li>Campaign Validity</li>
              </b>
              <ol style={{ listStyleType: 'lower-alpha' }}>
                <li>
                  Campaign End Date: The campaign will end on{' '}
                  {moment(watch('endDate')).format('DD MMM YYYY')}, upon which,
                  your affiliate link will also expire. for each successful
                  referral.
                </li>
              </ol>
              <b>
                <li>Affiliate Conduct</li>
              </b>
              <ol style={{ listStyleType: 'lower-alpha' }}>
                <li>
                  Appropriate Behavior: Tranding and{' '}
                  {
                    merchants.find(
                      (merchant) => merchant.id === watch('merchantId'),
                    )?.name
                  }{' '}
                  reserve the right to withhold commission payments if we deem
                  any behaviour to be inappropriate or negative is discovered.
                </li>
                <li>
                  Prohibited Actions: This includes, but is not limited to,
                  fraudulent activities, spamming, promotion of illegal products
                  or services, or misrepresenting the brand's products or
                  services.
                </li>
              </ol>
            </ol>
          )}
          <Button
            aria-label="add term"
            onClick={() => {
              append({ term: '' });
            }}
            endIcon={<AddIcon />}>
            Add Voucher Term
          </Button>
          {fields.map((item, i) => (
            <Controller
              key={item.id}
              name={`voucherTermsAndConditions.${i}.term`}
              control={control}
              rules={{
                required: 'Voucher terms and conditions are required',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="delete term"
                            disabled={
                              watch('voucherTermsAndConditions').length < 2
                            }
                            onClick={() => {
                              remove(i);
                              // append({ term: '' });
                            }}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  label={`Voucher Terms & Conditions #${i + 1}`}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              )}
            />
          ))}
          {/* <Controller
            name="voucherTermsAndConditions"
            control={control}
            rules={{
              required: 'Voucher terms and conditions are required',
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Voucher Terms & Conditions"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                multiline
                rows={3}
                fullWidth
              />
            )}
          /> */}
          {/* Image Upload Section */}
          <Controller
            name="images"
            control={control}
            rules={{ required: 'Please upload at least 1 campaign image' }}
            render={({ field, fieldState }) => (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Upload Images (up to 5)
                </Typography>
                <Typography variant="body1" fontStyle="italic" gutterBottom>
                  *Please ensure that the images' width and height are the same.
                </Typography>
                <input
                  {...{ ...field, value: undefined }}
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                  disabled={images && images.length >= 5}
                  data-testid="image-upload-input"
                />
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={images && images.length >= 5}>
                  {images && images.length >= 5
                    ? 'Maximum 5 images'
                    : 'Select Images'}
                </Button>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {(images || []).map((file, idx) => (
                    <Grid key={file.name + idx}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: 240,
                          height: 240,
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fafafa',
                        }}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview-${idx}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(idx)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(255,255,255,0.7)',
                          }}
                          aria-label="Remove image">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {fieldState.error && (
                  <Typography color="error" variant="caption">
                    {fieldState.error?.message}
                  </Typography>
                )}
              </Box>
            )}
          />
          {/* <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              console.log(getValues('images'));
            }}>
            adssa
          </Button> */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}>
            {updateMode ? 'Update' : 'Create Campaign'}
          </Button>
        </Stack>
      </Box>
    </PageBaseLayout>
  );
};

export default CreateNewCampaign;
